import { getAccessProfileQueryOptions } from "@packages/api-client/hooks/authorizationController";
import { listEstablishmentsQueryOptions } from "@packages/api-client/hooks/establishmentController";
import type { ListEstablishments200 } from "@packages/api-client/types/establishmentController";
import {
	Action,
	DEFAULT_LIMIT,
	DEFAULT_PAGE,
	type UserAccessProfile,
} from "@packages/shared-types";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { PaginationMeta } from "@/components/data-table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Pagination } from "@/components/ui/pagination";
import { hasPermission, useHasPermission } from "@/hooks/useAccessControl";
import { ensureQueryDataSurvivingCancel } from "@/lib/ensureQueryDataSurvivingCancel";
import { ForbiddenError } from "@/lib/forbiddenError";
import { CreateEstablishmentDialog } from "../../-components/CreateEstablishmentDialog";
import { EditEstablishmentDialog } from "../../-components/EditEstablishmentDialog";
import { SettingsLayout } from "../-components/SettingsLayout";
import { EstablishmentList } from "./-components/EstablishmentList";

type EstablishmentItem = ListEstablishments200["data"][number];

const searchSchema = z.object({
	page: z.number().catch(DEFAULT_PAGE),
	limit: z.number().catch(DEFAULT_LIMIT),
	search: z.string().catch(""),
	// Preserve the org-level farm scope param across this route's validation.
	farm: z.string().optional(),
});

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/settings/establishments/",
)({
	validateSearch: searchSchema,
	loader: async ({ context, params }) => {
		const orgId = params.orgId;

		// Preload access profile and guard: must have VIEW_ESTABLISHMENTS.
		const profile = await ensureQueryDataSurvivingCancel(
			context.queryClient,
			getAccessProfileQueryOptions({ organizationId: orgId }),
		);
		if (
			!hasPermission(profile as UserAccessProfile, Action.VIEW_ESTABLISHMENTS)
		) {
			throw new ForbiddenError("Access denied to establishments settings");
		}

		// Pre-fetch establishments data for initial render
		context.queryClient.ensureQueryData(
			listEstablishmentsQueryOptions(orgId, {
				page: DEFAULT_PAGE,
				limit: DEFAULT_LIMIT,
			}),
		);

		return { orgId };
	},
	component: EstablishmentsSettingsPage,
});

function EstablishmentsSettingsPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useParams();
	const search = Route.useSearch();

	// Local state for pagination and search
	const [currentPage, setCurrentPage] = useState(search.page);
	const [pageSize, setPageSize] = useState(search.limit);
	const [paginationMeta, setPaginationMeta] = useState({
		page: currentPage,
		limit: pageSize,
		total: 0,
		totalPages: 0,
		hasNextPage: false,
		hasPreviousPage: false,
	});
	const [searchTerm, setSearchTerm] = useState(search.search);
	const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(search.search);

	// Debounce search term to avoid too many API calls
	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedSearchTerm(searchTerm);
			setCurrentPage(1); // Reset to first page when search term changes
		}, 500); // 500ms delay

		return () => clearTimeout(timer);
	}, [searchTerm]);

	// Access control
	const checkPermission = useHasPermission();
	const canManageEstablishments = checkPermission(Action.MANAGE_ESTABLISHMENTS);

	// Pagination handlers
	const handlePageChange = useCallback((page: number) => {
		setCurrentPage(page);
	}, []);

	const handlePageSizeChange = useCallback((limit: number) => {
		setPageSize(limit);
		setCurrentPage(1); // Reset to first page when changing page size
	}, []);

	const handleSearchChange = useCallback((newSearchTerm: string) => {
		setSearchTerm(newSearchTerm);
		// Page reset is handled by useEffect when debouncedSearchTerm changes
	}, []);

	const handlePaginationMetaChange = useCallback((meta: PaginationMeta) => {
		setPaginationMeta(meta);
	}, []);

	// State for dialogs
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [editingEstablishment, setEditingEstablishment] =
		useState<EstablishmentItem | null>(null);

	return (
		<SettingsLayout
			activeTab="establishments"
			title={t("common.establishments")}
			testId="establishments-page"
			contentTestId="establishments-settings-main-content-area"
		>
			{/* Establishments Card */}
			<Card className="flex flex-col">
				<CardHeader className="pb-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
					{/* Search */}
					<div className="relative w-full sm:w-72">
						<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-base-content/40" />
						<Input
							type="search"
							placeholder={t("establishments.searchPlaceholder")}
							value={searchTerm}
							onChange={(e) => handleSearchChange(e.target.value)}
							className="pl-10 pr-10"
							data-testid="establishments-search-input"
							autoComplete="off"
							data-1p-ignore="true"
							data-lpignore="true"
							data-form-type="other"
						/>
						{searchTerm !== debouncedSearchTerm && (
							<div className="absolute right-3 top-1/2 transform -translate-y-1/2">
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
							</div>
						)}
					</div>

					{/* Create Button */}
					{canManageEstablishments && (
						<Button
							className="btn btn-primary text-primary-content w-full sm:w-auto"
							data-testid="create-establishment-button"
							onClick={() => setIsCreateDialogOpen(true)}
						>
							<Plus className="h-4 w-4 mr-2" />
							{t("common.createNew")}
						</Button>
					)}
				</CardHeader>

				<CardContent className="flex flex-col space-y-4">
					{/* Pagination */}
					<div className="shrink-0">
						<Pagination
							pagination={paginationMeta}
							onPageChange={handlePageChange}
							onPageSizeChange={handlePageSizeChange}
						/>
					</div>

					{/* Table */}
					<EstablishmentList
						organizationId={orgId}
						pagination={{
							page: currentPage,
							limit: pageSize,
						}}
						searchTerm={debouncedSearchTerm || undefined}
						canManageEstablishments={canManageEstablishments || false}
						onPaginationMetaChange={handlePaginationMetaChange}
						onEdit={setEditingEstablishment}
					/>
				</CardContent>
			</Card>

			{/* Create Dialog */}
			{canManageEstablishments && (
				<CreateEstablishmentDialog
					open={isCreateDialogOpen}
					onOpenChange={setIsCreateDialogOpen}
					orgId={orgId}
				/>
			)}

			{/* Edit Dialog */}
			<EditEstablishmentDialog
				establishment={editingEstablishment}
				onOpenChange={(open) => {
					if (!open) {
						setEditingEstablishment(null);
					}
				}}
				orgId={orgId}
			/>
		</SettingsLayout>
	);
}
